import React, { useState, useEffect, useRef } from 'react';
import { UploadImageContainer, UploadAndDisplayImage2 } from '../files/upload-image';

const InventoryGallery = ({ photos, onFilesSelected, onRemovePhoto, onUpdatePhotoComment, onUpdatePhotoTitle, uploadContainerStyle }) => {

    const handleAddImage = (fileArray) => {
        // fileArray is an array of File objects from the upload widget
        if (typeof onFilesSelected === 'function') {
            onFilesSelected(fileArray);
        }
    };

    // Lightbox state for full-size image preview
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const touchRef = useRef({});
    const imgContainerRef = useRef(null);

    const selectedImage = (selectedIndex !== null && photos && photos[selectedIndex]) ? (photos[selectedIndex].pathIncSASToken || photos[selectedIndex].path || photos[selectedIndex].url || photos[selectedIndex].dataUrl) : null;

    useEffect(() => {
        function onKey(e) {
            if (selectedIndex === null) return;
            if (e.key === 'Escape') {
                setSelectedIndex(null);
                setScale(1);
                setOffset({ x: 0, y: 0 });
            } else if (e.key === 'ArrowLeft') {
                setSelectedIndex(i => (i > 0 ? i - 1 : i));
                setScale(1);
                setOffset({ x: 0, y: 0 });
            } else if (e.key === 'ArrowRight') {
                setSelectedIndex(i => (photos && i < photos.length - 1 ? i + 1 : i));
                setScale(1);
                setOffset({ x: 0, y: 0 });
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedIndex, photos]);

    // Basic wheel zoom
    const onWheel = (e) => {
        if (selectedIndex === null) return;
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setScale(s => Math.max(1, Math.min(5, +(s + delta).toFixed(2))));
        }
    };

    // Touch handlers: pinch-to-zoom and pan
    const onTouchStart = (e) => {
        if (!e.touches) return;
        touchRef.current.start = { time: Date.now(), touches: Array.from(e.touches).map(t => ({ id: t.identifier, x: t.clientX, y: t.clientY })) };
        touchRef.current.last = touchRef.current.start;
    };

    const onTouchMove = (e) => {
        if (!touchRef.current.start || !e.touches) return;
        const touches = Array.from(e.touches).map(t => ({ id: t.identifier, x: t.clientX, y: t.clientY }));
        if (touches.length === 1) {
            // pan
            const dx = touches[0].x - touchRef.current.last.touches[0].x;
            const dy = touches[0].y - touchRef.current.last.touches[0].y;
            setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
        } else if (touches.length === 2) {
            // pinch
            const prev = touchRef.current.last.touches;
            const prevDist = Math.hypot(prev[0].x - prev[1].x, prev[0].y - prev[1].y);
            const currDist = Math.hypot(touches[0].x - touches[1].x, touches[0].y - touches[1].y);
            const ratio = currDist / prevDist;
            setScale(s => Math.max(1, Math.min(5, +(s * ratio).toFixed(2))));
        }
        touchRef.current.last = { touches };
    };

    const onTouchEnd = (e) => {
        touchRef.current.start = null;
        touchRef.current.last = null;
    };

    return (
        <div>
            <div className="mb-2">
                <UploadImageContainer style={uploadContainerStyle}>
                    {/* the upload widget attaches to .custom-file-container by data-upload-id */}
                    <UploadAndDisplayImage2 addImage={handleAddImage} multiple={true} maxFileCount={10} />
                </UploadImageContainer>
            </div>

            <div className="row mt-2">
                {photos && photos.map(p => {
                    const previewSrc = p.thumbPathIncSASToken || p.pathIncSASToken || p.thumbPath || p.path || p.dataUrl || '';
                    const fullSrc = p.pathIncSASToken || p.pathIncludingSasToken || p.path || p.url || p.dataUrl || previewSrc;
                    return (
                        <div className="col-6 col-md-4 mb-3" key={p.id}>
                            <div className="card">
                                <button type="button" className="btn p-0" onClick={(e) => { e.preventDefault(); setSelectedIndex(photos.findIndex(x => (x.pathIncSASToken || x.path || x.url || x.dataUrl) === fullSrc)); }} style={{ border: 'none', background: 'transparent' }}>
                                    <img src={previewSrc} alt={p.title || ''} className="card-img-top" style={{ objectFit: 'cover', height: 140 }} />
                                </button>
                                <div className="card-body p-2">
                                    <input className="form-control form-control-sm mb-1" placeholder="Title (optional)" value={p.title || ''} onChange={(e) => onUpdatePhotoTitle && onUpdatePhotoTitle(p.id, e.target.value)} />
                                    <textarea className="form-control form-control-sm mt-1" placeholder="Photo comment" value={p.comment || ''} onChange={(e) => onUpdatePhotoComment(p.id, e.target.value)} />
                                    <div className="d-flex justify-content-between mt-2">
                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => onRemovePhoto(p.id)}>Remove</button>
                                        <span className="small text-muted">{(p.dataUrl || p.path || '').length > 0 ? Math.round(((p.dataUrl ? p.dataUrl.length : 0)*3)/4/1024) + ' KB' : ''}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Lightbox/modal preview for full-size image */}
            {selectedImage && (
                <div
                    className="inventory-lightbox"
                    onClick={() => { setSelectedIndex(null); setScale(1); setOffset({ x: 0, y: 0 }); }}
                    style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}
                >
                    <div
                        ref={imgContainerRef}
                        onClick={(e) => e.stopPropagation()}
                        onWheel={onWheel}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        style={{ position: 'relative', maxWidth: '95%', maxHeight: '95%', overflow: 'auto', WebkitOverflowScrolling: 'touch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <button type="button" onClick={() => { setSelectedIndex(null); setScale(1); setOffset({ x: 0, y: 0 }); }} style={{ position: 'absolute', top: 8, right: 8, zIndex: 3100 }} className="btn-close btn-close-white" aria-label="Close"></button>
                        <img src={selectedImage} alt="Full size" style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transition: 'transform 0.05s linear' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryGallery;
